import React from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { FaChevronDown, FaTimes } from 'react-icons/fa';
/**
 * Dropdown select component. Primarily used in Connected Devices Device Graph feature for Device Selection due to unique behavior.
 *
 * Supports single and multiple selection modes, optional checkboxes,
 * grouped options, and disabled options.
 *
 * @param {Object} props
 * @param {Array} [props.options=[]] - Array of options to display. Each option can be a primitive or an object with `label` and `value` keys.
 * @param {string|number|Array} [props.selectedOption] - Currently selected option(s). For multiple selection, this should be an array.
 * @param {(value: string|number|Array) => void} props.setSelected - Setter function to update the selected option(s).
 * @param {boolean} [props.multiple=false] - Whether multiple selection is allowed.
 * @param {boolean} [props.haveCheckboxes=false] - Whether to show checkboxes next to options.
 * @param {(event: React.SyntheticEvent, value: string|number, index: number) => void} [props.onChange] - Optional callback when an option is selected.
 * @param {React.CSSProperties} [props.triggerStyle] - Optional styles for the dropdown trigger button.
 * @param {React.CSSProperties} [props.contentStyle] - Optional styles for the dropdown menu content.
 * @param {(option: any, index: number) => boolean} [props.isOptionDisabled] - Function to determine if an option should be disabled.
 * @param {() => void} [props.onClearAll] - Optional callback to clear all selections in multiple mode.
 * @returns {JSX.Element}
 */
export default function DropdownSelect({
  options = [],
  selectedOption,
  setSelected,
  multiple = false,
  haveCheckboxes = false,
  onChange,
  triggerStyle,
  contentStyle,
  isOptionDisabled = () => false,
  onClearAll,
}) {
  const isOptionSelected = (option) => {
    const value = optionValue(option);
    if (multiple) return Array.isArray(selectedOption) && selectedOption.includes(value);
    return selectedOption === value;
  };

  const optionValue = (option) =>
    option && typeof option === 'object' ? (option.value ?? option.label ?? option) : option;
  const optionLabel = (option) =>
    option && typeof option === 'object' ? (option.label ?? option.value) : option;

  const handleClearAll = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!multiple || !Array.isArray(selectedOption) || selectedOption.length === 0) return;
    if (typeof onClearAll === 'function') onClearAll();
    else setSelected([]);
  };

  const handleSelect = (index, event, option) => {
    const value = optionValue(option);
    if (typeof onChange === 'function') onChange(event, value, index);
    if (!multiple) return setSelected(value);
    if (!Array.isArray(selectedOption)) return setSelected([value]);
    return selectedOption.includes(value)
      ? setSelected(selectedOption.filter((existing) => existing !== value))
      : setSelected([...selectedOption, value]);
  };

  const dropdownLabel = () => {
    if (!multiple) {
      const opt = options.find((o) => optionValue(o) === selectedOption);
      return optionLabel(opt) || '';
    }
    if (!Array.isArray(selectedOption) || selectedOption.length === 0) return 'Select Options';
    return selectedOption
      .map((selectedValue) => {
        const opt = options.find((o) => optionValue(o) === selectedValue);
        return optionLabel(opt) || '';
      })
      .join(', ');
  };

  const firstOption = options && options.length ? options[0] : null;
  const optionsAreGrouped =
    firstOption && typeof firstOption === 'object' && 'group' in firstOption;

  const renderSimple = () =>
    options.map((option, index) => {
      const disabled = !!isOptionDisabled(option, index);
      const value = optionValue(option) ?? index;
      return (
        <Dropdown.Item
          key={`${value}-${index}`}
          className="rounded-2"
          active={optionValue(option) === selectedOption}
          disabled={disabled}
          style={{ opacity: disabled ? 0.6 : 1 }}
          onClick={(e) => {
            if (disabled) return;
            handleSelect(index, e, option);
          }}
        >
          {haveCheckboxes || multiple ? (
            <Form.Check
              type="checkbox"
              label={optionLabel(option)}
              disabled={disabled}
              checked={isOptionSelected(option)}
              onChange={() => {}}
              readOnly
              style={{ opacity: disabled ? 0.6 : 1, pointerEvents: 'none' }}
            />
          ) : (
            optionLabel(option)
          )}
        </Dropdown.Item>
      );
    });

  const renderGrouped = () => {
    const grouped = new Map();
    options.forEach((option, index) => {
      const groupKey = option.group ?? 'Ungrouped';
      if (!grouped.has(groupKey)) grouped.set(groupKey, []);
      grouped.get(groupKey).push({ option, index });
    });
    return Array.from(grouped.entries()).map(([groupKey, items]) => (
      <div key={`group-${groupKey}`} className="mb-1">
        <div className="bg-primary text-white border-bottom border-2 border-white text-center fw-bold px-2 py-1">
          {groupKey}
        </div>
        {items.map(({ option, index }) => {
          const disabled = !!isOptionDisabled(option, index);
          const value = optionValue(option) ?? index;
          return (
            <Dropdown.Item
              key={`${value}-${index}-${groupKey}`}
              className="rounded-2"
              active={optionValue(option) === selectedOption}
              disabled={disabled}
              style={{ opacity: disabled ? 0.6 : 1 }}
              onClick={(e) => {
                if (disabled) return;
                handleSelect(index, e, option);
              }}
            >
              {haveCheckboxes || multiple ? (
                <Form.Check
                  type="checkbox"
                  label={optionLabel(option)}
                  disabled={disabled}
                  checked={isOptionSelected(option)}
                  onChange={() => {}}
                  readOnly
                  style={{ opacity: disabled ? 0.6 : 1, pointerEvents: 'none' }}
                />
              ) : (
                optionLabel(option)
              )}
            </Dropdown.Item>
          );
        })}
      </div>
    ));
  };

  return (
    <Dropdown autoClose="outside" className="w-100">
      <Dropdown.Toggle
        as={Button}
        variant="primary"
        className="w-100 d-flex align-items-center rounded-2 no-caret"
        style={triggerStyle}
      >
        <span className="ms-2 text-truncate max-w-lg-3xs" style={{ maxWidth: 170 }}>
          {dropdownLabel()}
        </span>
        {multiple && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear selected devices"
            className="text-white p-0 ms-auto me-2 d-inline-flex align-items-center text-decoration-none"
            onClick={handleClearAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClearAll(e);
            }}
          >
            <FaTimes />
          </span>
        )}
        <FaChevronDown className="me-2" />
      </Dropdown.Toggle>
      <Dropdown.Menu className="p-1 w-100 z-999" style={{ ...contentStyle }}>
        {optionsAreGrouped ? renderGrouped() : renderSimple()}
      </Dropdown.Menu>
    </Dropdown>
  );
}
